import { useCallback, useContext, useEffect, useRef } from 'react';
import GridModeContext from 'contexts/GridModeContext';
import { TableActiveContext } from 'contexts/TableActiveContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { TableOnScreenContext } from 'contexts/TableOnScreenContext';
import { VisibleTileContext } from 'contexts/VisibleTileContext';
import { beginTableSnap, endTableSnap, isTableSnapping } from 'utils/tableSnapState';

import { getGridVisibility } from 'components/MultiTableGrid/gridScrollChannel';
import {
  isTableSwitching,
  TABLE_SWITCH_MS
} from 'components/RebindingTableView/tableSwitchTransition';

/**
 * `shouldSnap()` for the table this component belongs to (TableIdContext).
 *
 * Read it INSIDE the animation effect, never as a dependency: the whole point
 * is that closing the window must not re-run — and therefore re-fire — an
 * animation the arrival already snapped. The returned function is stable for a
 * given table, so it is safe to leave out of dependency arrays.
 */
export function useTableSnap(): () => boolean {
  // Read the context directly rather than through useTableSelector's useTableId
  // so an animation component's snap check never depends on how a caller (or a
  // test) has that module wired up.
  const gameID = useContext(TableIdContext);
  // A mounted-but-inert panel (outgoing mid-slide, or held by the carousel's
  // keep-alive so switching back costs no remount) applies state without ever
  // animating: nobody is watching, and the frames belong to the table that IS
  // being watched. It therefore also arrives already-correct — which is what
  // makes a switch back to it free. Always true where no provider exists
  // (tests, storybook) — see contexts/TableActiveContext.
  const isActive = useContext(TableActiveContext);
  // EXCEPT a cell of the multi-table grid the player can see. Since EN-10577
  // every grid cell has a TableActiveProvider, but there it is the
  // ambient-SOUND focus gate (only the selected cell is "active"), not "nobody
  // is watching". Without this, chips stopped flying from the pot to the winner
  // on every unfocused cell (EN-11150). Sound keeps its own active-table gate
  // (hooks/useSound), so this does not bring back the EN-10577 noise.
  //
  // "Can see" is the cell's share of the grid's scrolled row, read when the
  // animation asks: TableOnScreenContext is true for every cell of a shown grid,
  // including those scrolled past the row's edge (see its SCOPE note). Read
  // here rather than subscribed to, so scrolling never re-renders a felt. Still
  // inert: off-row cells and a grid parked behind single-table view (its
  // channel is cleared).
  //
  // The tiled carousel (two or more tables side by side) has the same split:
  // only the focused tile is active, every tile on its track is in plain view.
  // RebindingTableView marks those (contexts/VisibleTileContext); its track IS
  // the viewport, so no scroll share is needed. Still inert: tiles it holds
  // parked off-track, and every tile while a slide runs, whose frames belong to
  // the slide.
  const isGridMode = useContext(GridModeContext);
  const isOnScreen = useContext(TableOnScreenContext);
  const isVisibleTile = useContext(VisibleTileContext);
  return useCallback(() => {
    const isVisibleGridCell = isGridMode && isOnScreen && (getGridVisibility()[gameID] ?? 0) > 0;
    const isSettledVisibleTile = isVisibleTile && !isTableSwitching();
    return (!isActive && !isVisibleGridCell && !isSettledVisibleTile) || isTableSnapping(gameID);
  }, [gameID, isActive, isGridMode, isOnScreen, isVisibleTile]);
}

/**
 * The ARRIVAL half of the snap on its own: is this table inside its arrival or
 * reconnect-resync window?
 *
 * `useTableSnap` folds two different ideas together — "this table is replaying
 * a backlog" and "nobody is looking at this panel". That is right for ambient
 * table sound, where both mean silence. It is wrong for the cross-table
 * attention cues, whose entire job is to be heard from a table the player is
 * NOT looking at: the inactive half silences them everywhere and the cue is
 * lost (EN-10577 AC2 — this is exactly the regression the first cut shipped).
 *
 * A background table is not "arriving", it is simply off screen, so this drops
 * the inactive term and keeps the backlog suppression (AC3).
 *
 * It also depends only on the gameID, never on which panel is asking — so the
 * several mounted copies of one table all agree. That is what lets
 * `utils/attentionCues.claimCue` hand the cue to whichever copy asks first
 * without risking that copy being the one that then drops it.
 */
export function useTableArrivalSnap(): () => boolean {
  const gameID = useContext(TableIdContext);
  return useCallback(() => isTableSnapping(gameID), [gameID]);
}

/**
 * Owns one table's arrival window (see utils/tableSnapState).
 *
 * Mounted by the table root: the hold is taken during RENDER, because a child's
 * mount effect runs before its parent's effects — taking it in an effect here
 * would be too late to stop the first replayed animation. It is released one
 * painted frame after the table's current state is on screen (plus the tail of
 * a carousel slide, whose frames are exactly what this protects).
 *
 * `hydrated` is "this table's real state has arrived" — until then there is
 * nothing accurate to show, so the window stays open. That is what covers the
 * post-login and post-reconnect restores, where the subtree mounts empty and
 * the snapshot lands a moment later.
 */
export function useSnapOnArrival(gameID: string, hydrated: boolean): void {
  const heldRef = useRef<string | null>(null);
  const releasedRef = useRef(false);

  if (heldRef.current !== gameID) {
    // A grid cell can be re-pointed at another table in place (MTT balancing);
    // hand the old table's hold back before taking the new one.
    if (heldRef.current !== null && !releasedRef.current) endTableSnap(heldRef.current);
    heldRef.current = gameID;
    releasedRef.current = false;
    beginTableSnap(gameID);
  }

  const release = useCallback(() => {
    if (releasedRef.current || heldRef.current === null) return;
    releasedRef.current = true;
    endTableSnap(heldRef.current);
  }, []);

  useEffect(() => {
    if (!hydrated) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const frame = requestAnimationFrame(() => {
      // Mid-switch the carousel is still sliding; keep snapping until it lands
      // so nothing competes with it for frames.
      const delay = isTableSwitching() ? TABLE_SWITCH_MS : 0;
      timer = setTimeout(release, delay);
    });
    return () => {
      cancelAnimationFrame(frame);
      if (timer !== null) clearTimeout(timer);
    };
  }, [gameID, hydrated, release]);

  // Unmounting without ever hydrating must still hand the hold back — another
  // view of the same table (desktop grid cell) would otherwise stay snapped.
  useEffect(() => () => release(), [release]);

  // COMING BACK TO A KEPT-ALIVE PANEL. That panel never unmounted, so the mount
  // hold above does not re-open — yet the switch is still an arrival: effects
  // keyed on "am I the active table" re-run at that instant, and without a fresh
  // window one of them could animate or sound off a state change the player
  // never saw happen. Hold again for the same beat: one painted frame, plus the
  // tail of the slide.
  const isActive = useContext(TableActiveContext);
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    const becameActive = isActive && !wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!becameActive || !gameID) return undefined;

    beginTableSnap(gameID);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      endTableSnap(gameID);
    };
    let timer: ReturnType<typeof setTimeout> | null = null;
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(finish, isTableSwitching() ? TABLE_SWITCH_MS : 0);
    });
    return () => {
      cancelAnimationFrame(frame);
      if (timer !== null) clearTimeout(timer);
      finish();
    };
  }, [isActive, gameID]);
}
